import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { PlusIcon, EyeIcon, TrashIcon, StarIcon, UsersIcon } from '@heroicons/react/20/solid'
import { createCrudApi } from '../../lib/api'
import { ConciergeEventForm } from '../../components/forms/ConciergeEventForm'
import { Pagination } from '../../components/ui/Pagination'
import { useTranslation } from '../../hooks/useTranslation'
import type { ConciergeEvent, ConciergeEventInput } from '../../../shared/types'

const eventsApi = createCrudApi<ConciergeEvent, ConciergeEventInput>('concierge-events')

// Status badge component
function StatusBadge({ status }: { status: string }) {
  const colors = {
    upcoming: 'bg-green-100 text-green-800',
    past: 'bg-gray-100 text-gray-800',
    cancelled: 'bg-red-100 text-red-800',
  }
  return (
    <span className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${colors[status as keyof typeof colors] || colors.upcoming}`}>
      {status}
    </span>
  )
}

// Visibility badge component
function VisibilityBadge({ visibility }: { visibility: string }) {
  const colors = {
    public: 'bg-blue-100 text-blue-800',
    patron: 'bg-purple-100 text-purple-800',
  }
  return (
    <span className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${colors[visibility as keyof typeof colors] || colors.public}`}>
      {visibility}
    </span>
  )
}

// Category badge component
function CategoryBadge({ category }: { category: string }) {
  const colors = {
    hospitality: 'bg-amber-100 text-amber-800',
    music: 'bg-pink-100 text-pink-800',
    wine: 'bg-red-100 text-red-800',
    sport: 'bg-green-100 text-green-800',
    maritime: 'bg-cyan-100 text-cyan-800',
    auto: 'bg-orange-100 text-orange-800',
  }
  return (
    <span className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${colors[category as keyof typeof colors] || 'bg-gray-100 text-gray-800'}`}>
      {category}
    </span>
  )
}

export function ConciergeListPage() {
  const [showForm, setShowForm] = useState(false)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(20)
  const [categoryFilter, setCategoryFilter] = useState<string>('')
  const [statusFilter, setStatusFilter] = useState<string>('')
  const queryClient = useQueryClient()
  const { t } = useTranslation()

  const { data: response, isLoading } = useQuery({
    queryKey: ['concierge-events', { page, limit, category: categoryFilter, status: statusFilter }],
    queryFn: () => eventsApi.list({
      page,
      limit,
      filters: {
        ...(categoryFilter && { category: categoryFilter }),
        ...(statusFilter && { status: statusFilter }),
      },
    }),
  })

  const events = response?.data

  const createMutation = useMutation({
    mutationFn: eventsApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['concierge-events'] })
      setShowForm(false)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: eventsApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['concierge-events'] })
    },
  })

  if (isLoading) return <div>{t('common.loading')}</div>

  return (
    <>
      {/* Secondary column - Actions and filters */}
      <aside className="fixed inset-y-4 left-72 hidden w-[calc((100vw-18rem-2rem)/3)] overflow-y-auto bg-white px-4 py-6 sm:px-6 lg:px-8 xl:block rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)]">
        <div className="space-y-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{t('pages.concierge.title')}</h2>
            <p className="mt-1 text-sm text-gray-500">
              {t('pages.concierge.description')}
            </p>
          </div>

          <button
            type="button"
            onClick={() => setShowForm(!showForm)}
            className="w-full flex items-center justify-center gap-x-2 rounded-md px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:opacity-90 bg-brand-500"
          >
            <PlusIcon className="h-5 w-5" />
            {showForm ? t('common.cancel') : t('pages.concierge.addButton')}
          </button>

          <Link
            to="/concierge/partners"
            className="w-full flex items-center justify-center gap-x-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-center text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50"
          >
            <UsersIcon className="h-5 w-5" />
            {t('pages.concierge.managePartners')}
          </Link>

          {/* Filters */}
          <div className="border-t border-gray-200 pt-4">
            <h3 className="text-sm font-medium text-gray-900 mb-3">{t('common.filters')}</h3>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">{t('fields.category')}</label>
                <select
                  value={categoryFilter}
                  onChange={(e) => { setCategoryFilter(e.target.value); setPage(1) }}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
                >
                  <option value="">{t('common.all')}</option>
                  <option value="hospitality">{t('concierge.categories.hospitality')}</option>
                  <option value="music">{t('concierge.categories.music')}</option>
                  <option value="wine">{t('concierge.categories.wine')}</option>
                  <option value="sport">{t('concierge.categories.sport')}</option>
                  <option value="maritime">{t('concierge.categories.maritime')}</option>
                  <option value="auto">{t('concierge.categories.auto')}</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">{t('fields.status')}</label>
                <select
                  value={statusFilter}
                  onChange={(e) => { setStatusFilter(e.target.value); setPage(1) }}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
                >
                  <option value="">{t('common.all')}</option>
                  <option value="upcoming">{t('concierge.status.upcoming')}</option>
                  <option value="past">{t('concierge.status.past')}</option>
                  <option value="cancelled">{t('concierge.status.cancelled')}</option>
                </select>
              </div>
            </div>
          </div>

          {showForm && (
            <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
              <h3 className="text-sm font-medium text-gray-900 mb-4">{t('pages.concierge.newTitle')}</h3>
              <ConciergeEventForm
                onSubmit={(data) => createMutation.mutate(data)}
                onCancel={() => setShowForm(false)}
                loading={createMutation.isPending}
              />
            </div>
          )}

          <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
            <h3 className="text-sm font-medium text-gray-900 mb-3">{t('pages.concierge.stats.title')}</h3>
            <dl className="space-y-2">
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">{t('pages.concierge.stats.total')}</dt>
                <dd className="font-medium text-gray-900">{response?.total || 0}</dd>
              </div>
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">{t('pagination.page')}</dt>
                <dd className="font-medium text-gray-900">{response?.page || 1} {t('pagination.of')} {response?.totalPages || 1}</dd>
              </div>
            </dl>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="xl:pl-[calc((100vw-18rem-2rem)/3)] xl:ml-4 p-4 xl:p-0 xl:pr-4 xl:py-4">
        <div className="bg-white rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)] min-h-[calc(100vh-2rem)] px-4 py-10 sm:px-6 lg:px-8 lg:py-6 overflow-hidden">
          {/* Mobile header */}
          <div className="xl:hidden mb-6">
            <h1 className="text-2xl font-semibold text-gray-900">{t('pages.concierge.title')}</h1>
            <div className="mt-4 flex gap-2">
              <button
                type="button"
                onClick={() => setShowForm(!showForm)}
                className="flex-1 flex items-center justify-center gap-x-2 rounded-md px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:opacity-90 bg-brand-500"
              >
                <PlusIcon className="h-5 w-5" />
                {showForm ? t('common.cancel') : t('pages.concierge.addButton')}
              </button>
              <Link
                to="/concierge/partners"
                className="flex items-center justify-center gap-x-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-center text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50"
              >
                <UsersIcon className="h-5 w-5" />
              </Link>
            </div>
            {showForm && (
              <div className="mt-6 bg-white p-6 rounded-lg shadow">
                <h2 className="text-lg font-medium mb-4">{t('pages.concierge.newTitle')}</h2>
                <ConciergeEventForm
                  onSubmit={(data) => createMutation.mutate(data)}
                  onCancel={() => setShowForm(false)}
                  loading={createMutation.isPending}
                />
              </div>
            )}
          </div>

          {/* Table */}
          <div className="flow-root">
            <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
              <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                <div className="overflow-hidden shadow-sm ring-1 ring-gray-900/5 sm:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                          {t('concierge.fields.titleEn')}
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          {t('fields.category')}
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          {t('concierge.fields.eventDate')}
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          {t('concierge.fields.visibility')}
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          {t('fields.status')}
                        </th>
                        <th className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                          <span className="sr-only">{t('common.actions')}</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {events?.map((event) => (
                        <tr key={event.id} className="hover:bg-gray-50 transition-colors">
                          <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-6">
                            <div className="flex items-center gap-2">
                              {event.featured && (
                                <StarIcon className="h-4 w-4 text-yellow-500" />
                              )}
                              <div>
                                <div className="font-medium text-gray-900">{event.titleEn}</div>
                                {event.location && (
                                  <div className="text-gray-500 text-xs">{event.location}</div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            <CategoryBadge category={event.category} />
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {new Date(event.eventDate).toLocaleDateString()}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            <VisibilityBadge visibility={event.visibility} />
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            <StatusBadge status={event.status} />
                          </td>
                          <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                            <Link
                              to={`/concierge/${event.id}`}
                              className="inline-flex items-center gap-x-1 text-brand-600 hover:text-brand-900 mr-4"
                            >
                              <EyeIcon className="h-4 w-4" />
                              {t('common.view')}
                            </Link>
                            <button
                              onClick={() => deleteMutation.mutate(event.id)}
                              className="inline-flex items-center gap-x-1 text-red-600 hover:text-red-900"
                            >
                              <TrashIcon className="h-4 w-4" />
                              {t('common.delete')}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {response && (
                    <Pagination
                      page={response.page}
                      totalPages={response.totalPages}
                      total={response.total}
                      limit={response.limit}
                      onPageChange={setPage}
                      onLimitChange={(newLimit) => {
                        setLimit(newLimit)
                        setPage(1)
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
